/**
 * Toasts Domain
 * Simple toast notification system for displaying temporary messages
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

// ==================== TYPES ====================

export type ToastType = 'success' | 'error' | 'info' | 'warning';

interface Toast {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  duration?: number;
}

// ==================== STORE ====================

interface ToastsState {
  toasts: Toast[];
  addToast: (toast: Omit<Toast, 'id'>) => void;
  removeToast: (id: string) => void;
  /** Every toast gone at once; one still timed to leave just leaves nothing. */
  clear: () => void;
}

export const useToastsStore = create<ToastsState>()(
  devtools(
    (set) => ({
      toasts: [],

      addToast: (toast) => {
        const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const duration = toast.duration ?? 5000;

        set(
          (state) => ({
            toasts: [...state.toasts, { ...toast, id, duration }],
          }),
          false,
          'addToast'
        );

        // Auto-remove after duration
        if (duration > 0) {
          setTimeout(() => {
            set(
              (state) => ({
                toasts: state.toasts.filter((t) => t.id !== id),
              }),
              false,
              'removeToast'
            );
          }, duration);
        }
      },

      removeToast: (id) => {
        set(
          (state) => ({
            toasts: state.toasts.filter((t) => t.id !== id),
          }),
          false,
          'removeToast'
        );
      },

      clear: () => set({ toasts: [] }, false, 'clear'),
    }),
    {
      name: 'toasts-store',
    }
  )
);

// ==================== HELPER FUNCTIONS ====================

/**
 * Show an info toast: news that asks for nothing, such as a private chat
 * that has connected or ended. `duration` is the store's five seconds unless
 * given.
 */
export function showInfoToast(title: string, message?: string, duration?: number): void {
  useToastsStore.getState().addToast({ type: 'info', title, message, duration });
}

/**
 * Show a success toast: something the player asked for, done where the screen
 * does not show it — the team's resources added, a save written to its folder.
 */
export function showSuccessToast(title: string, message?: string): void {
  useToastsStore.getState().addToast({ type: 'success', title, message });
}

/**
 * Show an error toast
 */
export function showErrorToast(title: string, message?: string): void {
  useToastsStore.getState().addToast({ type: 'error', title, message });
}

/**
 * Show a warning toast: something to look at again that does not stop play,
 * such as a microphone that could not start.
 */
export function showWarningToast(title: string, message?: string): void {
  useToastsStore.getState().addToast({ type: 'warning', title, message });
}

// ==================== THE PLAYER'S ACTS ====================

/** The player closes a toast before its time is up. */
export function dismissToast(id: string): void {
  useToastsStore.getState().removeToast(id);
}
